import React, { useState } from "react";
import InputField from "../../ResuableCompoment/InputField";
import SelectField from "../../ResuableCompoment/SelectField";
import Button from "../../ResuableCompoment/Button";
import Statemaster from "../../../DummyJson/Statemaster.json";
import useApi from "../../CustomHooks/useApi";
import { checkUID } from "../../ResuableCompoment/ValidateAadhar";
import UserTypeModal from "../../ResuableCompoment/UserTypeModal";
import Popup from "../../ResuableCompoment/Popup";

const CreateRoleAdmin = () => {
    /* ================= USER TYPE ================= */
    const [userType, setUserType] = useState(null); // internal | external
    const [showTypePopup, setShowTypePopup] = useState(true);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    /* ================= FORM STATE ================= */
    const [formData, setFormData] = useState({
        fullName: "",
        fatherName: "",
        mobileNumber: "",
        address: "",
        email: "",
        dob: "",
        gender: "",
        panCard: "",
        aadhaarCard: "",
        state: "",
        district: "",
        role: "",
        shId: "",
        rmId: "",
        nhId: "",
        zhId: "",
        pincode: "",
        password: "",
        confirmPassword: ""
    });

    const [errors, setErrors] = useState({});
    const [roleOptions, setRoleOptions] = useState([]);
    const [nhOptions, setNhOptions] = useState([]);
    const [zhOptions, setZhOptions] = useState([]);
    const [rsmOptions, setRsmOptions] = useState([]);
    const [shOptions, setShOptions] = useState([]);

    /* ================= API ================= */
    const { callApi } = useApi("/staff/onboard/create/role", "POST");
    const { callApi: roleapicall } = useApi("/staff/onboard/get/roles", "GET");

    /* ================= VALIDATION ================= */
    const validateField = (name, value) => {
        let errorMsg = "";

        if (["fullName", "fatherName"].includes(name)) {
            if (!/^[A-Za-z\s]{1,100}$/.test(value)) {
                errorMsg = "Only alphabets & spaces allowed (max 100).";
            }
        }

        if (name === "mobileNumber") {
            if (!/^[6-9]\d{9}$/.test(value)) {
                errorMsg = "Invalid mobile number.";
            }
        }

        if (name === "email") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                errorMsg = "Invalid email format.";
            }
        }

        if (name === "dob") {
            const age =
                new Date().getFullYear() - new Date(value).getFullYear();
            if (age < 18) errorMsg = "Minimum age should be 18.";
        }

        if (name === "panCard") {
            if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)) {
                errorMsg = "Invalid PAN format.";
            }
            if (value && !["P", "F", "C"].includes(value[3])) {
                errorMsg = "4th letter must be P, F or C.";
            }
        }

        // if (name === "aadhaarCard") {
        //     if (!/^\d{12}$/.test(value)) {
        //         errorMsg = "Aadhaar must be 12 digits.";
        //     } else if (!checkUID(value)) {
        //         errorMsg = "Aadhaar Card Not Valid.";
        //     }
        // }

        if (name === "password" && userType === "internal") {
            if (value.length < 8) {
                errorMsg = "Password must be at least 8 characters.";
            }
        }

        if (name === "confirmPassword" && userType === "internal") {
            if (value !== formData.password) {
                errorMsg = "Passwords do not match.";
            }
        }

        return errorMsg;
    };

    /* ================= INPUT CHANGE ================= */
    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        setErrors((prev) => ({ ...prev, [name]: "" }));
        setFormData((prev) => ({ ...prev, [name]: value }));

        /* ---- Fetch hierarchy ONLY for EXTERNAL ---- */
        if (userType !== "external" || name !== "role") return;

        // Reset hierarchy selections on role change
        setFormData((prev) => ({
            ...prev,
            role: value,
            nhId: "",
            zhId: "",
            rmId: "",
            shId: ""
        }));

        // Map child role -> parent role
        const roleParentMap = {
            ZONAL_HEAD: "NATIONAL_HEAD",        // ZH -> NH
            REGIONAL_SALES_MANAGER: "ZONAL_HEAD",     // RM -> ZH
            STATE_HEAD: "REGIONAL_SALES_MANAGER",     // SH -> RM
            ASM: "STATE_HEAD"                   // ASM -> SH
        };

        const roleTypeParam = roleParentMap[value];
        if (!roleTypeParam) {
            setRoleOptions([]);
            return;
        }

        const response = await roleapicall(null, {
            role_type: roleTypeParam
        });

        if (response) {
            setRoleOptions(
                response.map((item) => ({
                    value: item.userId,
                    label: `${item.meta.fullName} (${item.mobile_number})`,
                    Rmlabel: item?.userHierarchyResponse?.parent?.name
                }))
            );
        }
    };


    /* ================= SUBMIT ================= */
    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {};
        Object.keys(formData).forEach((field) => {
            const err = validateField(field, formData[field]);
            if (err) newErrors[field] = err;
        });

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const payload = {
            aadhaar_card: formData.aadhaarCard,
            address: formData.address,
            district: formData.district,
            date_of_birth: formData.dob,
            email: formData.email,
            father_name: formData.fatherName,
            full_name: formData.fullName,
            gender: formData.gender,
            mobile_number: formData.mobileNumber,
            pan_card: formData.panCard,
            pincode: formData.pincode,
            role: formData.role,
            state: formData.state,
            ...(userType === "internal" && {
                password: formData.password,
                confirmPassword: formData.confirmPassword
            })
        };

        let parentId = null;

        if (userType === "external") {
            if (formData.role === "ZONAL_HEAD") parentId = formData.nhId;
            if (formData.role === "REGIONAL_SALES_MANAGER") parentId = formData.zhId;
            if (formData.role === "STATE_HEAD") parentId = formData.rmId;
            if (formData.role === "ASM") parentId = formData.shId;
        }

        const response = parentId
            ? await callApi(payload, { parent_id: parentId })
            : await callApi(payload);

        if (response) {
            setShowSuccessPopup(true);
        }
    };

    /* ================= ROLE OPTIONS ================= */
    const roleDropdownOptions =
        userType === "internal"
            ? [
                { value: "ONBOARDING", label: "ONBOARDING" },
                { value: "COMPLIANCE", label: "COMPLIANCE" }
            ]
            : [
                { value: "NATIONAL_HEAD", label: "NH" },
                { value: "ZONAL_HEAD", label: "ZH" },
                { value: "REGIONAL_SALES_MANAGER", label: "RSM" },
                { value: "STATE_HEAD", label: "SH" },
                { value: "ASM", label: "ASM" }
            ];

    const inputFields = [
        { label: "PAN Card", name: "panCard", type: "text" },
        { label: "Aadhaar Card", name: "aadhaarCard", type: "text", inputMode: "numeric" },
        { label: "Mobile Number", name: "mobileNumber", type: "text", inputMode: "numeric" },
        { label: "Full Name", name: "fullName", type: "text" },
        { label: "Father's Name", name: "fatherName", type: "text" },
        { label: "Address", name: "address", type: "text" },
        { label: "Email", name: "email", type: "email" },
        { label: "Date of Birth", name: "dob", type: "date" },
        { label: "Pin Code", name: "pincode", type: "text", inputMode: "numeric" }
    ];

    // ✅ Add password fields ONLY for internal
    if (userType === "internal") {
        inputFields.push(
            { label: "Password", name: "password", type: "password" },
            { label: "Confirm Password", name: "confirmPassword", type: "password" }
        );
    }

    return (
        <>
            {/* ========= POPUP 1 ========= */}
            {showTypePopup && (
                <UserTypeModal
                    onConfirm={(type) => {
                        setUserType(type);
                        setShowTypePopup(false);
                    }}
                />
            )}

            {/* ========= POPUP 2 ========= */}
            {showSuccessPopup && (
                <Popup
                    message="User created successfully"
                    onClose={() => setShowSuccessPopup(false)}
                    navigateTo="/totalcount"
                />
            )}

            {/* ========= FORM ========= */}
            <div className="h-full bg-[#EEF1F5] p-4">
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-[#0A2A4F]">
                        {userType === "internal"
                            ? "CREATE ROLE (ONBOARDING / COMPLIANCE)"
                            : "CREATE ROLE (RM / SH / ASM)"}
                    </h2>
                    <p className="text-sm text-gray-500">
                        Fill the details below to create a new role
                    </p>
                </div>

                <div className="bg-white rounded-xl border shadow-sm">
                    <form
                        className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6"
                        onSubmit={handleSubmit}
                    >
                        {inputFields.map((field, index) => (
                            <div key={index}>
                                <InputField
                                    label={field.label}
                                    type={field.type}
                                    name={field.name}
                                    placeholder={field.label}
                                    value={formData[field.name]}
                                    onChange={handleInputChange}
                                    inputMode={field.inputMode}
                                />
                                {errors[field.name] && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {errors[field.name]}
                                    </p>
                                )}
                            </div>
                        ))}


                        <SelectField
                            label="Gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            options={[
                                { value: "Male", label: "Male" },
                                { value: "Female", label: "Female" },
                                { value: "Other", label: "Other" }
                            ]}
                        />

                        <SelectField
                            label="State"
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            options={Statemaster.states.map((s) => ({
                                value: s.state,
                                label: s.state
                            }))}
                        />

                        <SelectField
                            label="District"
                            name="district"
                            value={formData.district}
                            onChange={handleInputChange}
                            options={
                                Statemaster.states.find(
                                    (s) => s.state === formData.state
                                )?.districts.map((d) => ({
                                    value: d,
                                    label: d
                                })) || []
                            }
                        />

                        <SelectField
                            label="Role"
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            options={roleDropdownOptions}
                        />

                        {/* ===== EXTERNAL ONLY ===== */}

                        {userType === "external" && formData.role === "ZONAL_HEAD" && (
                            <SelectField label="Select NH" name="nhId" value={formData.nhId} onChange={handleInputChange} options={roleOptions} />
                        )}

                        {userType === "external" && formData.role === "REGIONAL_SALES_MANAGER" && (
                            <SelectField label="Select ZH" name="zhId" value={formData.zhId} onChange={handleInputChange} options={roleOptions} />
                        )}

                        {userType === "external" && formData.role === "STATE_HEAD" && (
                            <SelectField label="Select RSM" name="rmId" value={formData.rmId} onChange={handleInputChange} options={roleOptions} />
                        )}

                        {userType === "external" && formData.role === "ASM" && (
                            <>
                                <SelectField label="Select SH" name="shId" value={formData.shId} onChange={handleInputChange} options={roleOptions} />
                            </>
                        )}

                        <div className="md:col-span-2 pt-4">
                            <Button
                                type="submit"
                                label="Create Role"
                                fullWidth
                                className="bg-[#0A2A4F] text-white"
                            />
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default CreateRoleAdmin;


